import { PencilIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef } from 'react';

import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo
} from '@mdxeditor/editor';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import CalendlyOnboardingScreen from 'src/features/organization/ui/CalendlyOnboardingScreen';
import MarkdownContent from 'src/features/product/product-detail/ui/updates-tab/markdown-content/MarkdownContent';
import { analyticsService } from 'src/services/analytics/analyticsService';
import {
  RFQ_BUILDER_CAVELA_DIRECT_DISMISSED,
  RFQ_BUILDER_CAVELA_DIRECT_PURCHASE_COMPLETED,
  RFQ_BUILDER_DOCUMENT_EDITED,
  RFQ_BUILDER_DOCUMENT_SAVED,
  RFQ_BUILDER_NAME_EDITED,
  RFQ_BUILDER_SOURCING_STARTED
} from 'src/services/analytics/rfqBuilderEvents';
import type { Rfq } from 'src/shared/types/rfq';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import { isImageMimeType } from 'src/shared/utils/fileTypeUtils';

import CavelaDirectPage from 'src/pages/cavela-direct-page/CavelaDirectPage';

import { cn } from 'src/shared/components/ui';
import { ButtonV2, Input, Typography } from 'src/shared/components/ui';

import { useRfqEditing } from '../../hooks/useRfqEditing';
import { useRfqSourcingFlow } from '../../hooks/useRfqSourcingFlow';
import BlobV2 from '../BlobV2';

import '@mdxeditor/editor/style.css';

/** Extracts the source value from a file's tags (e.g. 'source:user' -> 'user') */
const getFileSource = (tags: string[]): string => {
  const sourceTag = tags.find((tag) => tag.startsWith('source:'));

  return sourceTag ? sourceTag.replace('source:', '') : 'other';
};

/** Human-readable labels for each file source */
const SOURCE_LABELS: Record<string, string> = {
  user: 'Uploaded Files',
  reference: 'Reference Images',
  ai: 'AI Generated',
  rfq_extracted: 'Extracted from Document'
};

/** Defines the display order of file source groups */
const SOURCE_ORDER = ['user', 'reference', 'ai', 'rfq_extracted'];

interface RfqDrawerProps {
  rfq?: Rfq;
  generatedContent: string;
  isGenerating: boolean;
  currentGeneratedContent: string;
  isChatFinished: boolean;
  isDrawerOpen: boolean;
  onBack: () => void;
}

const RfqDrawer: React.FC<RfqDrawerProps> = ({
  rfq,
  generatedContent,
  isGenerating,
  currentGeneratedContent,
  isChatFinished,
  isDrawerOpen,
  onBack
}) => {
  const markdownContainerRef = useRef<HTMLDivElement>(null);
  const prevDrawerOpenRef = useRef<boolean>(false);
  const scrollToTopInProgressRef = useRef<boolean>(false);

  const user = useAuthStore((state) => state.user);

  const {
    isSourcingStarted,
    showCavelaDirectOverlay,
    productForCavelaDirect,
    orderQuotesForCavelaDirect,
    isFirstOrgProduct,
    showCavelaDirect,
    onStartSourcing,
    handleCavelaDirectDismiss,
    showPostRfqBookingScreen,
    postRfqCalendlyUrl,
    handlePostRfqBookingDismiss
  } = useRfqSourcingFlow({ rfq });

  const {
    isEditingName,
    setIsEditingName,
    editedName,
    setEditedName,
    isEditingMarkdown,
    setIsEditingMarkdown,
    editedMarkdown,
    setEditedMarkdown,
    isUpdatingMarkdown,
    mdxEditorRef,
    handleSaveName,
    handleCancelEdit,
    handleSaveMarkdown,
    handleCancelMarkdownEdit
  } = useRfqEditing({ rfq, generatedContent });

  /** Group RFQ files by their source tag, filtering out hidden files */
  const filesBySource = useMemo(() => {
    const files = rfq?.rfq?.files ?? [];
    const grouped: Record<string, UploadedFile[]> = {};

    for (const file of files) {
      if (file.tags.includes('visibility:hidden')) continue;

      const source = getFileSource(file.tags);

      if (!grouped[source]) {
        grouped[source] = [];
      }
      grouped[source].push(file);
    }

    return grouped;
  }, [rfq?.rfq?.files]);

  /** Sorted source keys based on SOURCE_ORDER, with unknown sources at the end */
  const sortedSourceKeys = useMemo(() => {
    const keys = Object.keys(filesBySource);

    return keys.sort((a, b) => {
      const indexA = SOURCE_ORDER.indexOf(a);
      const indexB = SOURCE_ORDER.indexOf(b);
      const orderA = indexA === -1 ? SOURCE_ORDER.length : indexA;
      const orderB = indexB === -1 ? SOURCE_ORDER.length : indexB;

      return orderA - orderB;
    });
  }, [filesBySource]);

  const hasFiles = sortedSourceKeys.length > 0;

  const findScrollableContainer = (): HTMLElement | null => {
    if (!markdownContainerRef.current) return null;

    try {
      const container = markdownContainerRef.current;

      if (!container.isConnected) return null;

      const containerOverflow = window.getComputedStyle(container).overflowY;

      if (containerOverflow === 'auto' || containerOverflow === 'scroll') {
        return container;
      }

      let scrollableParent: HTMLElement | null = container.parentElement;

      while (scrollableParent) {
        if (!scrollableParent.isConnected) return null;

        const overflowY = window.getComputedStyle(scrollableParent).overflowY;

        if (overflowY === 'auto' || overflowY === 'scroll') {
          return scrollableParent;
        }
        scrollableParent = scrollableParent.parentElement;
      }
    } catch {
      return null;
    }

    return null;
  };

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const scrollContainer = findScrollableContainer();

        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    });
  };

  // Scroll to top when drawer opens (uses 'auto' for an instant snap feel)
  useEffect(() => {
    const drawerJustOpened = isDrawerOpen && !prevDrawerOpenRef.current;

    prevDrawerOpenRef.current = isDrawerOpen;

    if (!drawerJustOpened || !markdownContainerRef.current) return;

    scrollToTopInProgressRef.current = true;

    const clearFlagTimeoutRef = {
      current: null as ReturnType<typeof setTimeout> | null
    };

    const timeoutId = setTimeout(() => {
      const scrollContainer = findScrollableContainer();

      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
      }
      clearFlagTimeoutRef.current = setTimeout(() => {
        scrollToTopInProgressRef.current = false;
      }, 100);
    }, 350);

    return () => {
      clearTimeout(timeoutId);
      if (clearFlagTimeoutRef.current) {
        clearTimeout(clearFlagTimeoutRef.current);
      }
      scrollToTopInProgressRef.current = false;
    };
  }, [isDrawerOpen]);

  // Scroll to bottom while content is streaming
  useEffect(() => {
    if (
      !isGenerating ||
      !isDrawerOpen ||
      !markdownContainerRef.current ||
      scrollToTopInProgressRef.current
    )
      return;

    const scrollToBottom = () => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const scrollContainer = findScrollableContainer();

          if (scrollContainer) {
            scrollContainer.scrollTo({
              top: scrollContainer.scrollHeight,
              behavior: 'smooth'
            });
          }
        }, 100);
      });
    };

    const rafId = requestAnimationFrame(scrollToBottom);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [generatedContent, isGenerating, isDrawerOpen]);

  // Scroll to top once generation completes
  useEffect(() => {
    if (!isChatFinished || isGenerating || !markdownContainerRef.current)
      return;

    scrollToTop();
  }, [isChatFinished, isGenerating]);

  return (
    <div
      ref={markdownContainerRef}
      className={cn(
        'h-full w-full flex-1 bg-gray-1 py-10 px-14 mobile:!pr-5 relative overflow-y-auto cavela-scrollbar mobile:mt-12',
        isChatFinished && 'mx-auto bg-white pb-32'
      )}
      style={{
        backgroundImage: `
          linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        backgroundColor: '#FFF'
      }}
    >
      <AnimatePresence>
        {isGenerating && currentGeneratedContent.length === 0 && (
          <motion.div
            key="blob-generating"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 2, ease: 'easeInOut' }
            }}
            exit={{
              opacity: 0,
              transition: { duration: 0.3, ease: 'easeOut' }
            }}
            className="absolute inset-0"
          >
            <BlobV2
              key="blob-v2-generating"
              className="w-full h-full"
              speed={0.2}
              numBlobs={6}
              isolation={30}
              lookAt={{ x: 0, y: 0, z: 0 }}
              zoom={1.5}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        style={{ pointerEvents: isSourcingStarted ? 'none' : 'auto' }}
        className="absolute left-0 right-0 mx-auto max-w-[640px] pb-[120px] px-5 pl-12"
      >
        <motion.div
          className="relative z-[999999]"
          initial={{ opacity: 1 }}
          animate={{
            opacity: isSourcingStarted ? 0 : 1,
            transition: { duration: 1, ease: 'easeInOut', delay: 2.5 }
          }}
        >
          {isEditingName && isChatFinished ? (
            <div className="flex items-center gap-2 mb-4">
              <Input
                value={editedName}
                onValueChange={setEditedName}
                size="lg"
                className="flex-1"
                classNames={{
                  input: 'text-2xl font-bold'
                }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveName();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
              />
              <ButtonV2
                onPress={() => {
                  analyticsService.trackEvent(RFQ_BUILDER_NAME_EDITED, {
                    rfq_id: rfq?.rfq?.id
                  });
                  handleSaveName();
                }}
                color="primary"
                size="sm"
              >
                Save
              </ButtonV2>
              <ButtonV2 onPress={handleCancelEdit} color="secondary" size="sm">
                Cancel
              </ButtonV2>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.5 }}
              className="flex items-center gap-2 mb-4"
            >
              <Typography
                variant="h3"
                weight="bold"
                className={cn(
                  isSourcingStarted &&
                    '!text-white transition-colors duration-2000'
                )}
              >
                {editedName || 'RFQ Document'}
              </Typography>
              {isChatFinished && !isSourcingStarted && !isGenerating && (
                <button
                  onClick={() => setIsEditingName(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Edit RFQ name"
                >
                  <PencilIcon className="size-5 text-gray-600" />
                </button>
              )}
            </motion.div>
          )}
        </motion.div>

        {generatedContent && (
          <motion.div
            className={cn(
              'prose prose-slate z-[999999] relative',
              isSourcingStarted &&
                'prose-invert [&_*]:!text-white transition-colors duration-2000'
            )}
            initial={{ opacity: 1 }}
            animate={{
              opacity: isSourcingStarted ? 0 : 1,
              transition: { duration: 1, ease: 'easeInOut', delay: 2.5 }
            }}
          >
            {isEditingMarkdown ? (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <MDXEditor
                  ref={mdxEditorRef as React.RefObject<MDXEditorMethods>}
                  markdown={editedMarkdown}
                  onChange={setEditedMarkdown}
                  plugins={[
                    headingsPlugin(),
                    listsPlugin(),
                    quotePlugin(),
                    thematicBreakPlugin(),
                    markdownShortcutPlugin(),
                    tablePlugin(),
                    linkPlugin(),
                    linkDialogPlugin(),
                    imagePlugin(),
                    toolbarPlugin({
                      toolbarContents: () => (
                        <>
                          <UndoRedo />
                          <BoldItalicUnderlineToggles />
                          <BlockTypeSelect />
                          <CreateLink />
                          <InsertTable />
                          <ListsToggle />
                        </>
                      )
                    })
                  ]}
                  contentEditableClassName="prose prose-slate max-w-none min-h-[400px] p-4 focus:outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1"
                />
              </div>
            ) : (
              <MarkdownContent
                markdownString={editedMarkdown || generatedContent}
                className={cn(
                  isSourcingStarted &&
                    'text-white transition-colors duration-2000'
                )}
              />
            )}
          </motion.div>
        )}

        {/* Files section — grouped by source, visible only after generation completes */}
        {generatedContent &&
          hasFiles &&
          !isEditingMarkdown &&
          !isGenerating && (
            <motion.div
              className={cn(
                'mt-8 z-[999999] relative',
                isSourcingStarted &&
                  'opacity-0 transition-opacity duration-1000'
              )}
              initial={{ opacity: 0 }}
              animate={{
                opacity: isSourcingStarted ? 0 : 1,
                transition: { duration: 0.5, ease: 'easeInOut', delay: 0.3 }
              }}
            >
              <div className="space-y-6">
                {sortedSourceKeys.map((source) => (
                  <div key={source}>
                    <Typography variant="body-1" weight="bold" className="mb-2">
                      {SOURCE_LABELS[source] ?? source}
                    </Typography>

                    <div className="flex flex-col gap-2">
                      {filesBySource[source].map((file) => {
                        const isImage = isImageMimeType(
                          file.content_type ?? ''
                        );

                        return (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white p-2"
                          >
                            {isImage ? (
                              <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-50">
                                <img
                                  src={file.url}
                                  alt={file.description || file.name}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                            ) : null}
                            <p className="min-w-0 truncate text-sm text-gray-700">
                              {file.description || file.name}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
      </div>

      <AnimatePresence>
        {!isGenerating && isChatFinished && !isSourcingStarted && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 h-20 border-t border-gray-4 bg-white flex items-center justify-center mobile:justify-start mobile:px-5 gap-4 z-[999999]"
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ duration: 0.4, ease: 'easeOut', delay: 0.9 }}
          >
            {isEditingMarkdown ? (
              <div className="flex gap-2 items-center justify-center mobile:justify-end w-full">
                <ButtonV2
                  onPress={handleCancelMarkdownEdit}
                  size="lg"
                  color="fourth"
                  isDisabled={isUpdatingMarkdown}
                >
                  Cancel
                </ButtonV2>
                <ButtonV2
                  onPress={() => {
                    analyticsService.trackEvent(RFQ_BUILDER_DOCUMENT_SAVED, {
                      rfq_id: rfq?.rfq?.id
                    });
                    handleSaveMarkdown();
                  }}
                  size="lg"
                  isLoading={isUpdatingMarkdown}
                >
                  Save Changes
                </ButtonV2>
              </div>
            ) : (
              <div className="flex gap-2 items-center w-full justify-center">
                <ButtonV2 onPress={onBack} size="lg" color="fourth">
                  Back
                </ButtonV2>
                <div className="flex gap-4 mobile:gap-2 items-center mobile:ml-auto">
                  <ButtonV2
                    onPress={() => {
                      analyticsService.trackEvent(RFQ_BUILDER_DOCUMENT_EDITED, {
                        rfq_id: rfq?.rfq?.id
                      });
                      setIsEditingMarkdown(true);
                    }}
                    size="lg"
                    color="tertiary"
                    startContent={
                      <PencilIcon className="size-4 pointer-events-none" />
                    }
                  >
                    Edit
                  </ButtonV2>
                  <ButtonV2
                    onPress={() => {
                      analyticsService.trackEvent(
                        RFQ_BUILDER_SOURCING_STARTED,
                        {
                          rfq_id: rfq?.rfq?.id
                        }
                      );
                      onStartSourcing();
                    }}
                    isDisabled={isSourcingStarted}
                    size="lg"
                  >
                    {isFirstOrgProduct ? '✨ Submit RFQ' : '✨ Start sourcing'}
                  </ButtonV2>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {showCavelaDirect && showCavelaDirectOverlay && (
        <div className="fixed inset-0 z-[100001]">
          <CavelaDirectPage
            product={productForCavelaDirect}
            orderQuotes={orderQuotesForCavelaDirect}
            onDismissAnimate={() => {
              analyticsService.trackEvent(RFQ_BUILDER_CAVELA_DIRECT_DISMISSED, {
                rfq_id: rfq?.rfq?.id
              });
              handleCavelaDirectDismiss();
            }}
            onPurchaseComplete={() => {
              analyticsService.trackEvent(
                RFQ_BUILDER_CAVELA_DIRECT_PURCHASE_COMPLETED,
                { rfq_id: rfq?.rfq?.id }
              );
              handleCavelaDirectDismiss();
            }}
          />
        </div>
      )}

      {isSourcingStarted && showPostRfqBookingScreen && (
        <div className="fixed inset-0 z-[100002] bg-white overflow-y-auto">
          <CalendlyOnboardingScreen
            calendlyUrl={postRfqCalendlyUrl}
            userName={user?.username ?? ''}
            description="Your RFQ has been submitted! Book a discovery call with our team while we start working on your product."
            onSkip={handlePostRfqBookingDismiss}
          />
        </div>
      )}

      <AnimatePresence>
        {isSourcingStarted && (
          <motion.div
            className="fixed inset-0 z-[99999]"
            initial={{ clipPath: 'inset(0% 0 100% 0)' }}
            animate={{ clipPath: 'inset(0% 0 0% 0)' }}
            exit={{ clipPath: 'inset(0% 0 100% 0)' }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              backgroundColor: '#000EEF'
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default RfqDrawer;
